import { useMemo, useState } from 'react';
import { Check, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildHandgripPayload,
  formatHandgripPercentile,
  formatHandgripChartAgeBand,
  getHandgripChartAxisConfig,
  getHandgripChartData,
  getHandgripPlotAge,
  resolveHandgripSex,
  summarizeHandgripResult,
  type HandgripChartImages,
  type HandgripPatientContext,
  type HandgripResultPayload,
  type HandgripSide,
} from '../handgrip/handgripDomain';
import AssessmentWeightAdjustmentField from '../components/AssessmentWeightAdjustmentField';
import { TOMKINSON_HANDGRIP_2025_PERCENTILES } from '../handgrip/handgripTomkinson2025';
import { normalizePatientHeightCm, normalizePatientWeightKg } from '../utils/patientMetrics';

interface HandgripTestProps {
  initial?: (HandgripResultPayload & { chartImages?: HandgripChartImages }) | null;
  patientContext?: HandgripPatientContext;
  onCancel?: () => void;
  onNext: (result: HandgripResultPayload & { chartImages?: HandgripChartImages }) => void;
}

export type { HandgripResultPayload };

const parseHandgripValue = (value: string): number | null => {
  const trimmed = value.trim().replace(',', '.');
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const buildInputSeries = (attempts?: number[] | null): string[] => {
  if (!Array.isArray(attempts) || !attempts.length) return ['', '', ''];
  return attempts.map((value) => value.toString().replace('.', ','));
};

export default function HandgripTest({ initial = null, patientContext, onCancel, onNext }: HandgripTestProps) {
  const initialPayload = initial ?? null;
  const baselineWeightKg = normalizePatientWeightKg(initialPayload?.patientContext?.weightKg ?? patientContext?.weightKg);
  const [dominantHand, setDominantHand] = useState<HandgripSide>(initialPayload?.dominantHand === 'left' ? 'left' : 'right');
  const [rightInputs, setRightInputs] = useState<string[]>(buildInputSeries(initialPayload?.right?.attempts));
  const [leftInputs, setLeftInputs] = useState<string[]>(buildInputSeries(initialPayload?.left?.attempts));
  const [notes, setNotes] = useState<string>(initialPayload?.notes ?? '');
  const [weightKg, setWeightKg] = useState<number | null>(baselineWeightKg);

  const sex = resolveHandgripSex(patientContext?.sex);
  const ageYears = typeof patientContext?.ageYears === 'number' ? patientContext.ageYears : null;
  const displayHeightCm = normalizePatientHeightCm(patientContext?.heightCm);
  const effectivePatientContext = useMemo(
    () => ({
      ...patientContext,
      weightKg,
    }),
    [patientContext, weightKg],
  );
  const plotAge = getHandgripPlotAge(ageYears);
  const ageOutsideNormRange = ageYears != null && (ageYears < 20 || ageYears > 100);

  const rightAttempts = useMemo(
    () => rightInputs.map(parseHandgripValue).filter((value): value is number => value != null),
    [rightInputs],
  );
  const leftAttempts = useMemo(
    () => leftInputs.map(parseHandgripValue).filter((value): value is number => value != null),
    [leftInputs],
  );

  const payloadPreview = useMemo(
    () =>
      buildHandgripPayload({
        dominantHand,
        rightAttempts,
        leftAttempts,
        notes,
        patientContext: effectivePatientContext,
      }),
    [dominantHand, effectivePatientContext, leftAttempts, notes, rightAttempts],
  );

  const summary = useMemo(() => summarizeHandgripResult(payloadPreview), [payloadPreview]);
  const canFinish = rightAttempts.length + leftAttempts.length > 0;

  const absoluteChartData = useMemo(() => getHandgripChartData(sex, 'absolute'), [sex]);
  const normalizedChartData = useMemo(() => getHandgripChartData(sex, 'normalized'), [sex]);
  const absoluteAxis = useMemo(
    () => getHandgripChartAxisConfig(absoluteChartData, summary.bestOverall, 'absolute'),
    [absoluteChartData, summary.bestOverall],
  );
  const normalizedAxis = useMemo(
    () => getHandgripChartAxisConfig(normalizedChartData, summary.normalizedOverall, 'normalized'),
    [normalizedChartData, summary.normalizedOverall],
  );

  const updateAttemptInput = (side: HandgripSide, index: number, value: string) => {
    const setter = side === 'right' ? setRightInputs : setLeftInputs;
    setter((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const addAttemptInput = (side: HandgripSide) => {
    const setter = side === 'right' ? setRightInputs : setLeftInputs;
    setter((current) => [...current, '']);
  };

  const removeAttemptInput = (side: HandgripSide, index: number) => {
    const setter = side === 'right' ? setRightInputs : setLeftInputs;
    setter((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const clearAttempts = () => {
    setRightInputs(['', '', '']);
    setLeftInputs(['', '', '']);
  };

  const handleFinish = () => {
    if (!canFinish) {
      toast.error('Informe ao menos uma tentativa para concluir.');
      return;
    }

    onNext(payloadPreview);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">Handgrip — entrada manual</p>
          <p className="text-sm opacity-90">
            Registre as tentativas em kg por mão. O sistema calcula percentis Tomkinson 2025, % do predito e prepara os
            gráficos para o laudo.
          </p>
        </div>
      </div>

      <div className="space-y-3 rounded-lg border p-4">
        <div className="grid gap-3 md:grid-cols-4">
          <div className="rounded border bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Sexo</div>
            <div className="font-semibold">{sex === 'M' ? 'Masculino' : 'Feminino'}</div>
          </div>
          <div className="rounded border bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Idade</div>
            <div className="font-semibold">{ageYears != null ? `${ageYears} anos` : '—'}</div>
          </div>
          <div className="rounded border bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Altura</div>
            <div className="font-semibold">
              {displayHeightCm != null ? `${displayHeightCm} cm` : '—'}
            </div>
          </div>
          <div className="rounded border bg-gray-50 p-3">
            <div className="text-xs text-gray-500">Dominância</div>
            <select
              className="mt-1 w-full rounded border bg-white px-2 py-1"
              value={dominantHand}
              onChange={(event) => setDominantHand(event.target.value as HandgripSide)}
            >
              <option value="right">Direita</option>
              <option value="left">Esquerda</option>
            </select>
          </div>
        </div>

        <AssessmentWeightAdjustmentField
          value={weightKg}
          baselineValue={baselineWeightKg}
          onChange={setWeightKg}
          helperText="Use quando o peso real da avaliação estiver diferente do cadastro. O ajuste é salvo apenas neste teste."
        />

        {ageYears == null ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
            Preencha a idade do paciente para habilitar percentil e % predito.
          </div>
        ) : null}

        {ageOutsideNormRange && plotAge != null ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
            As normas Tomkinson 2025 cobrem 20 a 100+ anos; para cálculo e plotagem usamos {plotAge} anos.
          </div>
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border bg-white p-3 shadow-sm">
          <div className="text-xs uppercase text-gray-500">Melhor geral</div>
          <div className="text-2xl font-extrabold text-blue-700">
            {summary.bestOverall != null ? `${summary.bestOverall.toFixed(1)} kg` : '—'}
          </div>
          <div className="text-xs text-gray-500">
            Percentil: {formatHandgripPercentile(summary.absolutePercentile)} • % predito:{' '}
            {summary.absolutePercentPredicted != null ? `${summary.absolutePercentPredicted.toFixed(0)}%` : '—'}
          </div>
        </div>

        <div className="rounded-lg border bg-white p-3 shadow-sm">
          <div className="text-xs uppercase text-gray-500">Normalizado</div>
          <div className="text-2xl font-extrabold text-gray-800">
            {summary.normalizedOverall != null ? summary.normalizedOverall.toFixed(2) : '—'}
          </div>
          <div className="text-xs text-gray-500">
            Percentil: {formatHandgripPercentile(summary.normalizedPercentile)} • % predito:{' '}
            {summary.normalizedPercentPredicted != null ? `${summary.normalizedPercentPredicted.toFixed(0)}%` : '—'}
          </div>
        </div>

        <div className="rounded-lg border bg-white p-3 shadow-sm">
          <div className="text-xs uppercase text-gray-500">Melhor direita</div>
          <div className="text-2xl font-extrabold text-gray-800">
            {summary.bestRight != null ? `${summary.bestRight.toFixed(1)} kg` : '—'}
          </div>
          <div className="text-xs text-gray-500">Média: {summary.meanRight != null ? `${summary.meanRight.toFixed(1)} kg` : '—'}</div>
        </div>

        <div className="rounded-lg border bg-white p-3 shadow-sm">
          <div className="text-xs uppercase text-gray-500">Melhor esquerda</div>
          <div className="text-2xl font-extrabold text-gray-800">
            {summary.bestLeft != null ? `${summary.bestLeft.toFixed(1)} kg` : '—'}
          </div>
          <div className="text-xs text-gray-500">Média: {summary.meanLeft != null ? `${summary.meanLeft.toFixed(1)} kg` : '—'}</div>
        </div>
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Tentativas (kg)</h3>
          <button type="button" className="text-sm text-red-600 hover:text-red-700" onClick={clearAttempts}>
            Limpar
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {([
            { side: 'right' as const, label: 'Direita', values: rightInputs },
            { side: 'left' as const, label: 'Esquerda', values: leftInputs },
          ] as const).map((column) => (
            <div key={column.side} className="rounded border bg-white p-3">
              <div className="mb-3 flex items-center justify-between">
                <div className="font-medium text-sm">{column.label}</div>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => addAttemptInput(column.side)}
                >
                  <Plus className="h-4 w-4" />
                  Adicionar
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {column.values.map((value, index) => (
                  <label key={`${column.side}-${index}`} className="text-sm">
                    {index + 1}a
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="number"
                        inputMode="decimal"
                        className="w-full rounded border px-2 py-1"
                        value={value}
                        onChange={(event) => updateAttemptInput(column.side, index, event.target.value)}
                        placeholder="kg"
                      />
                      {column.values.length > 3 ? (
                        <button
                          type="button"
                          onClick={() => removeAttemptInput(column.side, index)}
                          className="p-2 text-gray-500 hover:text-red-600"
                          aria-label="Remover tentativa"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Curvas percentílicas — absoluto</h3>
          <div className="text-xs text-gray-500">kg</div>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[720px] bg-white">
            <ResponsiveContainer width="100%" height={260}>
            <LineChart data={absoluteChartData} margin={{ top: 8, right: 16, bottom: 28, left: 18 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="age"
                type="number"
                domain={absoluteAxis.xDomain}
                ticks={absoluteAxis.xTicks}
                tickFormatter={(value) => (value === 100 ? '100+' : String(value))}
                label={{ value: absoluteAxis.xLabel, position: 'insideBottom', offset: -16 }}
                allowDataOverflow
              />
              <YAxis
                domain={absoluteAxis.yDomain}
                ticks={absoluteAxis.yTicks}
                label={{ value: absoluteAxis.yLabel, angle: -90, position: 'insideLeft' }}
                allowDataOverflow
                width={64}
              />
              <Tooltip
                labelFormatter={(label, items) => formatHandgripChartAgeBand(items?.[0]?.payload, label)}
                formatter={(value: unknown, name: string | number) => [
                  typeof value === 'number' ? `${value.toFixed(1)} kg` : value,
                  String(name),
                ]}
                itemSorter={(item) => -Number(item.value ?? 0)}
              />
              {TOMKINSON_HANDGRIP_2025_PERCENTILES.map((percentile) => (
                <Line
                  key={`abs-${percentile}`}
                  type="monotone"
                  dataKey={`P${percentile}`}
                  stroke={percentile === 50 ? '#2563eb' : '#94a3b8'}
                  strokeWidth={percentile === 50 ? 2 : 1}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
              {summary.bestOverall != null && plotAge != null ? (
                <ReferenceDot x={plotAge} y={summary.bestOverall} r={4} fill="#ef4444" stroke="#b91c1c" />
              ) : null}
            </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="text-xs text-gray-600">
          Linhas: P{TOMKINSON_HANDGRIP_2025_PERCENTILES.join(', P')}. P50 em destaque. Ponto vermelho = paciente.
        </div>
      </div>

      <div className="space-y-4 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Curvas percentílicas — normalizado</h3>
          <div className="text-xs text-gray-500">kg/m2</div>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[720px] bg-white">
            <ResponsiveContainer width="100%" height={260}>
            <LineChart data={normalizedChartData} margin={{ top: 8, right: 16, bottom: 28, left: 18 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="age"
                type="number"
                domain={normalizedAxis.xDomain}
                ticks={normalizedAxis.xTicks}
                tickFormatter={(value) => (value === 100 ? '100+' : String(value))}
                label={{ value: normalizedAxis.xLabel, position: 'insideBottom', offset: -16 }}
                allowDataOverflow
              />
              <YAxis
                domain={normalizedAxis.yDomain}
                ticks={normalizedAxis.yTicks}
                label={{ value: normalizedAxis.yLabel, angle: -90, position: 'insideLeft' }}
                allowDataOverflow
                width={64}
              />
              <Tooltip
                labelFormatter={(label, items) => formatHandgripChartAgeBand(items?.[0]?.payload, label)}
                formatter={(value: unknown, name: string | number) => [
                  typeof value === 'number' ? `${value.toFixed(1)} kg/m²` : value,
                  String(name),
                ]}
                itemSorter={(item) => -Number(item.value ?? 0)}
              />
              {TOMKINSON_HANDGRIP_2025_PERCENTILES.map((percentile) => (
                <Line
                  key={`norm-${percentile}`}
                  type="monotone"
                  dataKey={`P${percentile}`}
                  stroke={percentile === 50 ? '#2563eb' : '#94a3b8'}
                  strokeWidth={percentile === 50 ? 2 : 1}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
              {summary.normalizedOverall != null && plotAge != null ? (
                <ReferenceDot x={plotAge} y={summary.normalizedOverall} r={4} fill="#ef4444" stroke="#b91c1c" />
              ) : null}
            </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {displayHeightCm == null ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
            Para calcular e plotar o valor normalizado, preencha a altura do paciente.
          </div>
        ) : null}
      </div>

      <label className="block text-sm">
        Observações (opcional)
        <textarea
          className="mt-1 w-full rounded border px-2 py-1"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Ex.: posição, dominância, dor, validade, etc."
        />
      </label>

      <div className="flex items-center justify-between">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <button
          type="button"
          className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white disabled:opacity-50"
          disabled={!canFinish}
          onClick={handleFinish}
        >
          Concluir Handgrip <Check className="ml-1 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
